import { PartiposterProfile } from 'state/submissions'
import { api, PartipostApiResponse } from './index'
import { TopPerformersPayload } from 'state/campaignReport'
import { PLATFORM_ENUM, TASK_TYPE_ENUM } from './constants'
import { MAX_PAGE_SIZE } from 'state/review'

export interface GetCampaignOverviewResponse {
    total_completed: number
    total_interested: number
    total_follower_count: { [key: number]: number }
    total_dropout_reject: number
}

export const getCampaignOverview = (payload: { uuid: string }) => {
    const { uuid } = payload
    return api.get<PartipostApiResponse<GetCampaignOverviewResponse>>(
        `/campaign-reports/campaign-overview/?campaign=${uuid}`,
    )
}

export interface GetCampaignPerformanceResponse {
    follower_total: number | null
    tasks: {
        uuid: string
        title: string
        follower: number | null
        performance: {
            reach: number | null
            view: number | null
            engagement: number | null
            engagement_rate: number | null
            content_engagement_rate: number | null
        }
    }[]
    performance_total: {
        reach: number | null
        view: number | null
        engagement: number | null
        engagement_rate: number | null
        content_engagement_rate: number | null
    }
}

export const getCampaignPerformance = (payload: { uuid: string }) => {
    const { uuid } = payload
    return api.get<PartipostApiResponse<GetCampaignPerformanceResponse>>(
        `/campaign-reports/campaign-performance/?campaign=${uuid}`,
    )
}

export interface GetTaskPerformanceResponse {
    tasks: {
        uuid: string
        title: string
        type: TASK_TYPE_ENUM
        steps: {
            uuid: string
            title: string
            performance: {
                view_count: number | null
                reach_count: number | null
                impression_count: number | null
            }
        }[]
    }[]
}

export const getTaskPerformance = (payload: { uuid: string }) => {
    const { uuid } = payload
    return api.get<PartipostApiResponse<GetTaskPerformanceResponse>>(
        `/campaign-reports/task-performance/?campaign=${uuid}`,
    )
}

type SubmissionStatus = 'in_progress' | 'completed' | 'drop_out' | 'rejected'

export interface GetIndividualPerformanceResponse {
    count: number
    next: string | null
    previous: string | null
    results: {
        submission_uuid: string
        favorite: boolean
        submission_status: SubmissionStatus
        partiposter_profile: PartiposterProfile
        tasks: {
            uuid: string
            title: string
            campaign_report_st_uuid: string | null
            submitted_follower?: number
            submitted_url?: string
            platform: PLATFORM_ENUM
            performance?: {
                like: number | null
                total_engagement: number | null
                content_engagement_rate: number | null
            }
        }[]
    }[]
}

export interface GetIndividualPerformanceRequest {
    uuid: string
    offset?: number
    keywords?: string
    ordering?: string
    filteringParams?: {
        [key: string]: string[] | string | undefined
    }
}

export const getIndividualPerformance = (payload: GetIndividualPerformanceRequest) => {
    const { uuid, offset, keywords, ordering, filteringParams } = payload
    const parameters = {
        offset,
        limit: MAX_PAGE_SIZE,
        keywords,
        ordering: ordering,
        ...filteringParams,
    }
    return api.get<PartipostApiResponse<GetIndividualPerformanceResponse>>(
        `/campaign-reports/individual-performance/?campaign=${uuid}`,
        parameters,
    )
}

export interface GetIndividualPerformanceStatsResponse {
    profile_follower: {
        [key in PLATFORM_ENUM]: number
    }
    tasks: {
        uuid: string
        title: string
        submitted_follower: number
        platform: PLATFORM_ENUM
        performance: {
            comments: number
            views: number
        }
    }[]
}

export const getIndividualPerformanceStats = (payload: { uuid: string }) => {
    const { uuid } = payload
    return api.get<PartipostApiResponse<GetIndividualPerformanceStatsResponse>>(
        `campaign-reports/individual-performance/stats/?campaign=${uuid}`,
    )
}

export interface UpdateAmbassadorFavoriteRequest {
    partiposter_uuid: string
    brand_uuid: string
    favorited: boolean
}

export interface UpdateAmbassadorFavoriteResponse {
    data: {
        success: boolean
    }
}

export const updateAmbassadorFavorite = (payload: UpdateAmbassadorFavoriteRequest) =>
    api.patch<PartipostApiResponse<UpdateAmbassadorFavoriteResponse>>('/ambassadors/favorite/', payload)

export interface FilterOption {
    results: {
        key: string
        options: string[]
    }[]
}

export interface GetIndividualPerformanceFiltersRequest {
    uuid: string
}

export const getIndividualPerformanceFilters = (payload: GetIndividualPerformanceFiltersRequest) => {
    const { uuid } = payload
    return api.get<PartipostApiResponse<FilterOption>>(
        `/campaign-reports/individual-performance/filters/?campaign=${uuid}`,
    )
}

export interface GetTopPerformersResponse {
    tasks: {
        uuid: string
        title: string
        total_users: number
        ranking: {
            favorite: boolean
            partiposter_profile: PartiposterProfile
            files: []
            value: number
        }[]
    }[]
}

export const getTopPerformers = (payload: TopPerformersPayload) => {
    const { uuid, tab, top } = payload
    return api.get<PartipostApiResponse<GetTopPerformersResponse>>(
        `/campaign-reports/top-performers/?campaign=${uuid}`,
        { tab, top },
    )
}

export const downloadCampaignReport = (payload: { uuid: string }) => {
    const { uuid } = payload
    return api.get<PartipostApiResponse<void>>(`/campaign-reports/export/?campaign=${uuid}`)
}
